import React, { useState, useEffect } from "react";
import axios from "axios";
import { Spin, Collapse, Button, Dropdown, Drawer, Flex } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useParams, useNavigate, useSearchParams, useOutletContext, useLocation } from "react-router-dom";
import { transportType, bodyType } from "../categories";
import OrdersModal from "../../suggest-order/OrdersModal";
import DetailPageTitle from "../DetailPageTitle";
import * as activeOrdersConst from "../../../constants/activeOrders";
import * as deliveryStatus from "../../../constants/deliveryStatus";
import { loadType, shippingType, currency } from "../constants";
import dayjs from "dayjs";
import { markSearchListReturn } from "../../../utils/searchReturnState";

const Tractor = ({ transport }) => {
  return (
    <div className="select-transport__detail__imgs">
      {transport.tech_passport_of_tractor && (
        <div>
          <img alt="" src={transport.tech_passport_of_tractor} />
        </div>
      )}
      {transport.second_side_of_passport && (
        <div>
          <img alt="" src={transport.second_side_of_passport} />
        </div>
      )}
      {!transport.tech_passport_of_tractor &&
        !transport.second_side_of_passport && <p>Нет документов</p>}
    </div>
  );
};
const Trailer = ({ transport }) => {
  return (
    <div className="select-transport__detail__imgs">
      {transport.tech_passport_of_trailer && (
        <div>
          <img alt="" src={transport.tech_passport_of_trailer} />
        </div>
      )}
      {transport.second_side_of_passport_of_trailer && (
        <div>
          <img alt="" src={transport.second_side_of_passport_of_trailer} />
        </div>
      )}
      {!transport.tech_passport_of_trailer &&
        !transport.second_side_of_passport_of_trailer && <p>Нет документов</p>}
    </div>
  );
};
const DriverLicense = ({ profile }) => {
  return (
    <div className="select-transport__detail__imgs">
      {profile.driver_license.license_photo && (
        <div>
          <img alt="" src={profile.driver_license.license_photo} />
        </div>
      )}
      {profile.driver_license.license_photo_backside && (
        <div>
          <img alt="" src={profile.driver_license.license_photo_backside} />
        </div>
      )}
      {!profile.driver_license.license_photo &&
        !profile.driver_license.license_photo_backside && <p>Нет документов</p>}
    </div>
  );
};
const Doc = ({ profile }) => {
  return (
    <div className="select-transport__detail__imgs">
      {profile.passport.passport_photo && (
        <div>
          <img alt="" src={profile.passport.passport_photo} />
        </div>
      )}
      {profile.passport.passport_photo_backside && (
        <div>
          <img alt="" src={profile.passport.passport_photo_backside} />
        </div>
      )}
      {!profile.passport.passport_photo &&
        !profile.passport.passport_photo_backside && <p>Нет документов</p>}
    </div>
  );
};
const License = ({ profile }) => {
  return (
    <div className="select-transport__detail__imgs">
      {profile.license.image && (
        <div>
          <img alt="" src={profile.license.image} />
        </div>
      )}
      {!profile.license.image && <p>Нет документов</p>}
    </div>
  );
};
const Patent = ({ profile }) => {
  return (
    <div className="select-transport__detail__imgs">
      {profile.patent.image && (
        <div>
          <img alt="" src={profile.patent.image} />
        </div>
      )}
      {!profile.patent.image && <p>Нет документов</p>}
    </div>
  );
};

const TransportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setScrollTo, setShowOnMap, drivers } = useOutletContext();
  const orderUuid = searchParams.get("o");
  const driverId = searchParams.get("d");
  const [loading, setLoading] = useState(true);
  const [transport, setTransport] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState(null);
  const [isOffer, setIsOffer] = useState(false);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [drawerOpen, setDrawerOpen] = useState(true);

  const handleBack = () => {
    const fromSearchList = location.state?.fromSearchList;
    if (fromSearchList === "orders" || fromSearchList === "transports") {
      markSearchListReturn(fromSearchList);
      navigate(location.state?.returnTo || (fromSearchList === "orders" ? "/loads" : "/transports"), {
        replace: true,
      });
      return;
    }

    const canGoBack = window.history.length > 1;
    canGoBack ? navigate(-1) : navigate("/");
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDrawerOpen(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleCloseDrawers = () => setDrawerOpen(false);
    window.addEventListener('closeMobileDrawers', handleCloseDrawers);
    return () => window.removeEventListener('closeMobileDrawers', handleCloseDrawers);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('mobile-other-level-view', isMobile);

    return () => {
      document.body.classList.remove('mobile-other-level-view');
    };
  }, [isMobile]);

  useEffect(() => {
    const body = document.querySelector("body");
    body.classList.add("big-grid");
    body.classList.remove("small-grid");
  }, [])

  useEffect(() => {
    const el = document.querySelector(".middle-sidebar__wrapper");
    if (el) {
      el.scrollTop = 0;
    }
    fetchTransport();

    // Check if this is an offer view based on URL query parameters
    if (orderUuid && driverId) {
      setIsOffer(true);
    }
  }, [id, orderUuid, driverId]);

  const fetchTransport = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const { data, status } = await axios.get(
        `${process.env.REACT_APP_API_URL}/transports/`,
        {
          headers: {
            Authorization: `JWT ${window.localStorage.getItem("access")}`,
          },
        },
      );
      if (status === 200) {
        // `id` is a vehicle id (cabinet search links) or, for legacy links, the owner's user id
        const _transport =
          data.find((t) => String(t.id) === String(id)) || data.find((t) => t.owner.id === id);
        setTransport(_transport);
        if (_transport && drivers) {
          const _dr = drivers.find((d) => d.id === _transport.owner.id);
          setShowOnMap(null);
          if (_dr) {
            setScrollTo({
              latitude: _dr.latitude,
              longitude: _dr.longitude
            });
          }
        }
        fetchProfile(_transport?.owner?.id || id);
      }
    } catch (error) {
      console.error("Error fetching transport details:", error);
      setError("Не удалось загрузить данные о транспорте");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async (ownerId) => {
    if (!ownerId) return;

    try {
      const { data } = await axios({
        url: `${process.env.REACT_APP_API_URL}/auth/profile/`,
        method: "GET",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });
      const _profile = data.find((p) => p.user.id === ownerId);
      setProfile(_profile);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const acceptOffer = async () => {
    if (!orderUuid || !driverId) {
      console.error("Missing order UUID or driver ID for accepting offer");
      return;
    }

    try {
      const { data } = await axios({
        url: `${process.env.REACT_APP_API_URL}/orders/${orderUuid}/`,
        data: {
          driver: driverId,
          status: activeOrdersConst.ACTIVE,
          delivery_status: deliveryStatus.TAKEN,
        },
        method: "PATCH",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });

      if (data) {
        alert("Заказ успешно передан водителю!");
        navigate("/notifications");
      }
    } catch (error) {
      console.error("Error accepting offer:", error);
      alert("Произошла ошибка при принятии предложения");
    }
  };

  const rejectOffer = async () => {
    const { status } = await axios({
      url: `${process.env.REACT_APP_API_URL}/offers/?driver_id=${driverId}&order_id=${orderUuid}`,
      method: "DELETE",
      headers: {
        Authorization: `JWT ${window.localStorage.getItem("access")}`,
      },
    });
    if (status == 204) {
      alert("Вы успешно отказали водителю!");
      navigate("/my-orders");
    }
  };

  // Items definition moved directly into the Collapse component

  if (loading) {
    return (
      <div className="select-transport order-detail">
        <DetailPageTitle title="Детали транспорта" onBack={handleBack} />
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div className="transport-search-full-screen">
            <Flex align="center" gap="middle">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            </Flex>
          </div>
          <p>Загрузка информации о транспорте...</p>
        </div>
      </div>
    );
  }

  if (error || !transport) {
    return (
      <div className="select-transport order-detail">
        <DetailPageTitle title="Детали транспорта" onBack={handleBack} />
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h3>{error || "Транспорт не найден"}</h3>
          <Button
            type="primary"
            onClick={handleBack}
          >
            Вернуться на главную
          </Button>
        </div>
      </div>
    );
  }

  console.log(["t", transport]);

  const contactName =
    transport.contact_name ||
    `${transport.owner.first_name || ""} ${transport.owner.last_name || ""}`.trim();
  const contactPhone = transport.contact_phone || transport.owner.phone_number;

  let destData = null;

  if (transport.owner?.online === 1 || (transport.owner?.location_from && transport.owner?.location_to)) {
    destData = {
      location_from: transport.owner?.location_from,
      from_country: transport.owner?.from_country,
      from_country_code: transport.owner?.from_country_code,
      location_to: transport.owner?.location_to,
      to_country: transport.owner?.to_country,
      to_country_code: transport.owner?.to_country_code,
      distance: transport.owner?.distance,
      from_date: transport.owner?.from_date,
      to_date: transport.owner?.to_date
    };
  } else if (transport.owner?.online !== 1 && transport.owner?.latest_order) {
    const order = transport.owner.latest_order;
    const fromLocation = order.from_address || (order.from && order.from.length > 0 && order.from[0]?.location_name) || "";
    const toLocation = order.to_address || (order.to && order.to.length > 0 && order.to[0]?.location_name) || "";

    if (fromLocation && toLocation) {
      destData = {
        location_from: fromLocation,
        from_country: order.from_country || (order.from && order.from.length > 0 && order.from[0]?.country),
        from_country_code: order.from_country_code || (order.from && order.from.length > 0 && order.from[0]?.country_code),
        location_to: toLocation,
        to_country: order.to_country || (order.to && order.to.length > 0 && order.to[0]?.country),
        to_country_code: order.to_country_code || (order.to && order.to.length > 0 && order.to[0]?.country_code),
        distance: order.distance,
        from_date: order.from_date,
        to_date: order.to_date
      };
    }
  }

  const content = (
    <div className="select-transport order-detail">
      <DetailPageTitle title="Детали транспорта" onBack={handleBack} />
      <div className="select-transport__wrapper">
        <div className="transport" style={{ paddingBottom: "0" }}>
          <div className="transport__wrapper">
            <div className="transport__driver pd-25">
              <div className="transport__driver__media">
                <img src={transport.owner.avatar} alt="" />
              </div>
              <div className="transport__driver__info">
                <div className="transport__driver__info__id">
                  ID {transport.owner.id}
                </div>
                <div className="transport__driver__info__name">
                  {transport.owner.first_name} {transport.owner.last_name}
                </div>
              </div>
              <div className="transport__driver__rating">
                <div className="transport__driver__rating__icon" />
                <div className="transport__driver__rating">5.0</div>
                <div className="transport__driver__rating__comment-icon" />
                <div className="transport__driver__rating">0</div>
              </div>
            </div>
          </div>
        </div>
        {destData && (
          <div className="transport__destinations pd-25">
            <div className="transport__destinations__left">
              {destData?.location_from?.split(", ")[0] !== destData?.from_country && <div className="transport__destination__city">
                {destData?.location_from?.split(", ")[0]}
              </div>}
              {destData?.location_from?.split(", ")[0] === destData?.from_country && <div className="transport__destination__city">
                &nbsp;
              </div>}
              <div className="transport__destination__icon__wrapper transport__destination__icon__wrapper--from">
                <div className="transport__destination__icon transport__destination__icon--from">
                  A
                </div>
              </div>
              <div className="transport__destination__country">
                <strong>{destData?.from_country_code}</strong>{destData?.from_country_code && destData?.from_country ? ", " : ""}{destData?.from_country}
              </div>
            </div>
            <div className="transport__destinations__middle">
              <div className="transport__destinations__middle__border" />
              <div className="transport__destinations__middle__distance">
                <div className="transport__destinations__middle__distance__icon" />
                {destData?.distance}
              </div>
            </div>
            <div className="transport__destinations__right">
              {destData?.location_to?.split(", ")[0] !== destData?.to_country && <div className="transport__destination__city">
                {destData?.location_to?.split(", ")[0]}
              </div>}
              {destData?.location_to?.split(", ")[0] === destData?.to_country && <div className="transport__destination__city">
                &nbsp;
              </div>}
              <div className="transport__destination__icon__wrapper transport__destination__icon__wrapper--to">
                <div className="transport__destination__icon transport__destination__icon--to">
                  B
                </div>
              </div>
              <div className="transport__destination__country">
                <strong>{destData?.to_country_code}</strong>{destData?.to_country_code && destData?.to_country ? ", " : ""}{destData?.to_country}
              </div>
            </div>
          </div>
        )}
        {destData?.from_date &&
          destData?.to_date && (
            <div
              className="select-transport__dates"
              style={{ marginBottom: "0", marginTop: "15px" }}
            >
              <div className="select-transport__dates__icon" />
              <div className="order-detail__section__item__label">
                Дата погрузки:
              </div>
              <div className="order-detail__section__item__value">
                с{" "}
                {dayjs(destData.from_date)
                  .locale("ru")
                  .format("DD MMMM")}{" "}
                по{" "}
                {dayjs(destData.to_date).locale("ru").format("DD MMMM")}
              </div>
            </div>
          )}
        <div className="order-form__section__title">
          <span className="order-form__section__title__text">
            Информация о транспорте
          </span>
        </div>
        <div className="order-detail__section">
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">
              Тип перевозки:
            </div>
            <div className="order-detail__section__item__value">
              {shippingType(transport.owner.shipping_type)}
            </div>
          </div>
          <br />
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">
              Категория авто:
            </div>
            <div className="order-detail__section__item__value">
              {transport.category
                ? transportType(transport.category).label
                : ""}
            </div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">
              Типа кузова:
            </div>
            <div className="order-detail__section__item__value">
              {transport.body_type ? bodyType(transport.body_type) : ""}
            </div>
          </div>
          <br />
          {transport.type_of_load && transport.owner.online === 3 && (
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип загрузки:
              </div>
              {transport.type_of_load.map((type, index) => (
                <div
                  className="order-detail__section__item__value"
                  key={index}
                  style={{ marginRight: "5px" }}
                >
                  {loadType(type)}
                </div>
              ))}
            </div>
          )}
          {transport.owner.status && transport.owner.online === 1 && (
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип загрузки:
              </div>
              <div
                className="order-detail__section__item__value"
                style={{ marginRight: "5px" }}
              >
                {loadType(transport.owner.status)}
              </div>
            </div>
          )}
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">
              Грузоподъемность:
            </div>
            <div className="order-detail__section__item__value">
              {transport.owner.online === 1
                ? transport.owner.weight
                : transport.max_load_weight}{" "}
              т.
            </div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Объем:</div>
            <div className="order-detail__section__item__value">
              {transport.owner.online === 1
                ? transport.owner.volume
                : transport.max_load_volume}{" "}
              м3.
            </div>
          </div>
          <br />
          <div className="order-detail__section__item">
            <div
              className="order-detail__section__item__label"
              style={{ display: "flex" }}
            >
              {/* <div className="select-transport__detail__label__icon select-transport__detail__label__icon--tractor" style={{height: '19px'}} /> */}
              Тягач:
            </div>
            <div className="order-detail__section__item__value">
              {transport.model}
            </div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Марка:</div>
            <div className="order-detail__section__item__value">
              {transport.mark}
            </div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Год:</div>
            <div className="order-detail__section__item__value">
              {transport.trailer_issue_year}
            </div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">
              Гос. номер:
            </div>
            <div className="order-detail__section__item__value">
              {transport.government_number}
            </div>
          </div>
          <br />
          {transport.is_trailer && (
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">Прицеп</div>
              <div className="order-detail__section__item__value">
                {transport.trailer}
              </div>
            </div>
          )}
          {transport.is_trailer && (
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">Год</div>
              <div className="order-detail__section__item__value">
                {transport.tractor_issue_year}
              </div>
            </div>
          )}
          {transport.is_trailer && (
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Гос. номер
              </div>
              <div className="order-detail__section__item__value">
                {transport.trailer_number}
              </div>
            </div>
          )}
        </div>
        <div className="order-form__section__title">
          <span className="order-form__section__title__text">Примечание</span>
        </div>
        {transport.comment && (
          <div
            className="order-form__section__text"
            style={{
              fontWeight: "600",
              minHeight: "100px",
              border: "1px solid #ccc",
              borderRadius: "5px",
              padding: "10px",
            }}
          >
            {transport.comment && (
              <div className="order-detail__section__item">
                <div className="order-detail__section__item__value">
                  {transport.comment}
                </div>
              </div>
            )}
          </div>
        )}
        {!transport.comment && (
          <div className="no-comment">
            Водитель не написал условия. Если есть вопросы уточните
          </div>
        )}
        {transport.owner.online === 1 && (
          <div className="order-form__section__title">
            <span className="order-form__section__title__text">Стоимость</span>
          </div>
        )}
        {transport.owner.online === 1 && (
          <div className="order-detail__section">
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Запрашиваемая ставка:
              </div>
              {transport.owner.price_from && !!transport.owner.currency && (
                <div className="order-detail__section__item__value">
                  {transport.owner.price_from}{" "}
                  {currency(transport.owner.currency)}
                </div>
              )}
              {!(transport.owner.price_from && !!transport.owner.currency) && (
                <div className="order-detail__section__item__value">
                  Ждем предложение
                </div>
              )}
            </div>
          </div>
        )}
        <div className="order-form__section__title">
          <span className="order-form__section__title__text">Контакты</span>
        </div>
        <div className="order-detail__section">
          <div className="order-detail__about">
            <div className="order-detail__about-person">
              <div
                className="order-detail__about-person__icon"
                style={{ border: "none", backgroundColor: "transparent" }}
              >
                <div
                  className="transport__driver__media"
                  style={{ width: "30px", height: "30px", minWidth: '30px' }}
                >
                  <img
                    src={transport.owner.avatar}
                    alt=""
                    style={{
                      objectFit: "cover",
                      height: "100%",
                      maxWidth: "100%",
                    }}
                  />
                </div>
              </div>
              <div className="order-detail__about-person__name">
                {contactName}
              </div>

              <Dropdown
                placement="topRight"
                trigger={["click"]}
                dropdownRender={() => (
                  <div className="order-detail__about-person__contacts-dropdown">
                    <div className="contact-item">
                      <a href={`tel:${contactPhone}`}>
                        {contactPhone}
                      </a>
                    </div>
                    {transport.owner?.profile?.whatsapp_number && (
                      <div className="contact-item">
                        <div
                          className="order-detail__about-person__contacts__whatsapp"
                          onClick={() =>
                            window.open(
                              `https://wa.me/${transport.owner.profile.whatsapp_number.replace(/[^0-9]/g, "")}`,
                              "_blank",
                            )
                          }
                        />
                      </div>
                    )}
                    {transport.owner?.profile?.telegram_nickname && (
                      <div className="contact-item">
                        <div
                          className="order-detail__about-person__contacts__telegram"
                          onClick={() =>
                            window.open(
                              `https://t.me/${transport.owner?.profile?.telegram_nickname.replace("@", "")}`,
                              "_blank",
                            )
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              >
                <div className="order-detail__about-person__call-icon" />
              </Dropdown>
            </div>
          </div>
        </div>
        {/* Documents Collapse - using direct HTML structure */}
        <div className="order-form__section__title">
          <span className="order-form__section__title__text">Документы</span>
        </div>
        <div className="order-detail__section" style={{ marginTop: "10px" }}>
          <Collapse>
            <Collapse.Panel
              key="1"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Технический паспорт тягача</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {transport?.tractor ? (
                  <div className="order-detail__document-img">
                    <Tractor transport={transport} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="2"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Технический паспорт прицепа</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {transport?.trailer ? (
                  <div className="order-detail__document-img">
                    <Trailer transport={transport} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="3"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Водительское удостоверение</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {profile?.driver_license ? (
                  <div className="order-detail__document-img">
                    <DriverLicense profile={profile} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="4"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Паспорт водителя</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {profile?.document ? (
                  <div className="order-detail__document-img">
                    <Doc profile={profile} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="5"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Лицензия</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {profile?.company_profile?.license ? (
                  <div className="order-detail__document-img">
                    <License profile={profile} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>

            <Collapse.Panel
              key="6"
              header={
                <div
                  className="order-detail__section__item"
                  style={{ marginBottom: 0 }}
                >
                  <div className="order-detail__section__item__label">
                    <strong>Патент</strong>
                  </div>
                </div>
              }
            >
              <div className="order-detail__documents">
                {profile?.company_profile?.patent ? (
                  <div className="order-detail__document-img">
                    <Patent profile={profile} />
                  </div>
                ) : (
                  <p>Документ отсутствует</p>
                )}
              </div>
            </Collapse.Panel>
          </Collapse>
        </div>
        <br />
        {isOffer && (
          <div
            className="select-transport__bottom transport__bottom-controls--oy"
            style={{ marginBottom: "80px" }}
          >
            <Button
              type="primary"
              size="large"
              onClick={acceptOffer}
              style={{ marginTop: "8px" }}
            >
              Принять предложение
            </Button>
            &nbsp; &nbsp;
            <Button
              size="large"
              onClick={rejectOffer}
              style={{ marginTop: "8px" }}
              className="my-custom-btn bordered"
            >
              Отклонить предложение
            </Button>
          </div>
        )}
        {!isOffer && (
          <div
            className="select-transport__bottom transport__bottom-controls--oy"
            style={{ marginBottom: "80px" }}
          >
            <Button type="primary" onClick={() => setShowModal(true)}>
              Преложить груз
            </Button>
          </div>
        )}
        {showModal && (
          <OrdersModal
            showModal={showModal}
            setShowModal={setShowModal}
            driver={profile}
          />
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        closable={false}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        height="auto"
        push={false}
        className="mobile-only"
        rootClassName="mobile-drawer-menu-container mobile-drawer-other-level"
        styles={{
          body: { padding: '10px 15px 0', backgroundColor: '#F0F4F7' }
        }}
      >
        <div style={{ maxHeight: 'calc(100vh - 10px)', overflowY: 'auto' }}>
          {content}
        </div>
      </Drawer>
    );
  }

  return content;
};

export default TransportDetail;
